import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

const TOLERANCE_PX = 150;

type UseJumpToBottomProps = {
  containerRef: React.RefObject<HTMLDivElement>;
  messageCount: number;
  isMessageLoading: boolean;
  enabled: boolean;
};

export const useJumpToBottom = ({
  containerRef,
  messageCount,
  isMessageLoading,
  enabled = false,
}: UseJumpToBottomProps) => {
  const [showJumpButton, setShowJumpButton] = useState(false);

  const checkOverflow = useCallback<() => void>(() => {
    if (!enabled) {
      return;
    }

    const el = containerRef.current;
    if (!el || messageCount === 0) {
      return;
    }

    const hasOverflow = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - TOLERANCE_PX;

    setShowJumpButton(!isMessageLoading && hasOverflow && !isAtBottom);
  }, [containerRef, messageCount, enabled, isMessageLoading]);

  const handleScroll = useCallback<() => void>(() => {
    if (!enabled) {
      return;
    }

    checkOverflow();
  }, [checkOverflow, enabled]);

  const scrollToBottom = () => {
    if (!enabled) {
      return;
    }

    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: 'smooth',
    });
    setShowJumpButton(false);
  };

  // resize observer to check if content overflows container
  useEffect(() => {
    if (!enabled) {
      return () => {};
    }

    const el = containerRef.current;
    const observer = new ResizeObserver(() => {
      checkOverflow();
    });

    if (el) {
      observer.observe(el);
    }
    return () => {
      observer.disconnect();
    };
  }, [checkOverflow, containerRef, enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const el = containerRef.current;
    if (!el) {
      return;
    }

    if (!isMessageLoading && messageCount > 0) {
      const hasOverflow = el.scrollHeight - el.clientHeight > TOLERANCE_PX;
      setShowJumpButton(hasOverflow);
    }
  }, [isMessageLoading, containerRef, messageCount, enabled]);

  return {
    showJumpButton,
    scrollToBottom,
    handleScroll,
  };
};
